import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import {
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineCreditCard,
  HiOutlineExclamationTriangle,
  HiOutlineServerStack,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineGlobeAlt,
  HiOutlineClock,
  HiOutlineMegaphone,
  HiOutlineCloud,
  HiOutlineCloudArrowUp,
  HiOutlineDocumentText,
  HiOutlineArrowPath,
  HiOutlineBolt,
  HiOutlineShieldCheck,
  HiOutlineBellAlert,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineEye,
} from 'react-icons/hi2'

/**
 * Superadmin mission-control dashboard.
 *
 * Top → bottom layout:
 *   1. Alert strip       — items needing immediate attention
 *   2. Activity pulse    — last-24h deltas (signups, active, uploads, connections)
 *   3. KPI grid          — current totals (subscribers, owners, paid, payment due)
 *   4. Cloud-storage     — connections, files-by-provider, events 24h, migrations, recent issues
 *   5. Storage breakdown — total GB + by plan + by provider
 *   6. Top accounts      — heaviest storage consumers
 *   7. Geography         — owners/employees by country (existing)
 *   8. Recent signups    — latest registrations
 *   9. Quick actions     — red gradient panel (existing, expanded)
 */

const POLL_INTERVAL_MS = 30_000

const SuperAdminDashboard = () => {
  const navigate = useNavigate()
  const { showError } = useAlerts()

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [statistics, setStatistics] = useState(null)
  const [cloudStats, setCloudStats] = useState(null)
  const [activity, setActivity] = useState(null)
  const [topAccounts, setTopAccounts] = useState(null)
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null)

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true)
    try {
      const [statRes, cloudRes, activityRes, topRes] = await Promise.all([
        superadminAPI.getStatistics(),
        superadminAPI.getCloudStorageStats(),
        superadminAPI.getActivityFeed(24),
        superadminAPI.getTopAccounts(8),
      ])

      if (statRes?.success) setStatistics(statRes.data?.data ?? statRes.data)
      else if (!silent) showError('Failed to load statistics')

      if (cloudRes?.success) setCloudStats(cloudRes.data?.data ?? cloudRes.data)
      if (activityRes?.success) setActivity(activityRes.data?.data ?? activityRes.data)
      if (topRes?.success) setTopAccounts(topRes.data?.data ?? topRes.data)

      setLastRefreshedAt(new Date())
    } catch (error) {
      if (!silent) showError('Failed to refresh dashboard')
      console.error('Dashboard fetch error', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [showError])

  useEffect(() => {
    fetchAll(false)
    const t = setInterval(() => fetchAll(true), POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [fetchAll])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!statistics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No statistics available.</p>
      </div>
    )
  }

  const { subscriber_counts, geography, storage } = statistics

  return (
    <div className="space-y-6">
      {/* ─────────────────────────────── Header ─────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">System Dashboard</h1>
            <LivePulse />
          </div>
          <p className="text-gray-600 mt-1 text-sm">
            Real-time overview of OVACS — auto-refreshes every 30 seconds.
            {lastRefreshedAt && (
              <span className="ml-2 text-gray-400">
                Last updated {lastRefreshedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => fetchAll(false)}
          disabled={refreshing}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors flex items-center gap-2"
        >
          <HiOutlineArrowPath className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {/* ─────────────────────────── 1. Alert strip ─────────────────────────── */}
      <AlertsStrip
        subscriberCounts={subscriber_counts}
        cloudStats={cloudStats}
        navigate={navigate}
      />

      {/* ────────────────────────── 2. Activity pulse ───────────────────────── */}
      <PulseRow activity={activity} />

      {/* ──────────────────────────── 3. KPI cards ──────────────────────────── */}
      <Section title="Subscriber overview" icon={HiOutlineUserGroup}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Users" value={subscriber_counts.total_users} icon={HiOutlineUsers} color="bg-info-soft0" />
          <StatCard title="Account Owners" value={subscriber_counts.total_owners} icon={HiOutlineUserGroup} color="bg-brand-soft0" />
          <StatCard title="Employees" value={subscriber_counts.total_employees} icon={HiOutlineUsers} color="bg-indigo-500" />
          <StatCard title="Paid Subscribers" value={subscriber_counts.paid_plan_subscribers} icon={HiOutlineCreditCard} color="bg-emerald-500" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <StatCard title="Free Plan" value={subscriber_counts.free_plan_subscribers} icon={HiOutlineUserGroup} color="bg-success-soft0" />
          <StatCard title="Payment Due" value={subscriber_counts.payment_due} icon={HiOutlineExclamationTriangle} color="bg-warning-soft0" badge={subscriber_counts.payment_due > 0 ? 'Action' : null} />
          <StatCard title="Expired Free" value={subscriber_counts.expired_free_plan} icon={HiOutlineClock} color="bg-danger-soft0" badge={subscriber_counts.expired_free_plan > 0 ? 'Expired' : null} />
          <StatCard title="Inactive Accounts" value={subscriber_counts.inactive_owners} icon={HiOutlineClock} color="bg-gray-500" />
        </div>
      </Section>

      {/* ──────────────────────── 4. Cloud-storage health ────────────────────── */}
      <CloudStorageSection stats={cloudStats} navigate={navigate} />

      {/* ─────────────────────────── 5. Storage breakdown ─────────────────────── */}
      <Section title="Storage usage" icon={HiOutlineServerStack}>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Total across all accounts</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{storage.total_storage_gb} <span className="text-base text-gray-500 font-normal">GB</span></p>
            </div>
            <div className="text-right text-sm text-gray-500">
              {storage.storage_by_plan?.reduce((a, p) => a + (p.account_count || 0), 0) || 0} active plans
            </div>
          </div>

          {/* Storage by provider — uses cloudStats.files.by_provider */}
          {cloudStats?.files?.by_provider?.length > 0 && (
            <div className="border-t border-gray-100 pt-4 mb-4">
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-3">By storage provider</h4>
              <div className="space-y-2">
                {cloudStats.files.by_provider.map((row) => (
                  <ProviderBar key={row.storage_provider} row={row} totalBytes={
                    cloudStats.files.by_provider.reduce((a, r) => a + (r.bytes || 0), 0)
                  } />
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-3">By subscription plan</h4>
            <div className="space-y-2">
              {storage.storage_by_plan?.length > 0 ? storage.storage_by_plan.map((plan, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{plan.plan__name}</p>
                    <p className="text-xs text-gray-500">{plan.account_count} accounts</p>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">{plan.total_storage_gb} GB</p>
                </div>
              )) : <p className="text-sm text-gray-500">No subscription storage data yet.</p>}
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── 6. Top accounts ─────────────────────────── */}
      <TopAccountsSection data={topAccounts} navigate={navigate} />

      {/* ──────────────────────────── 7. Geography ──────────────────────────── */}
      <Section title="Geographic distribution" icon={HiOutlineGlobeAlt}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GeoCard title="Owners by country" rows={geography.owners_by_country} dotColor="bg-brand-soft0" />
          <GeoCard title="Employees by country" rows={geography.employees_by_country} dotColor="bg-indigo-500" />
        </div>
      </Section>

      {/* ────────────────────────── 8. Recent signups ────────────────────────── */}
      {activity?.recent_signups?.length > 0 && (
        <Section title="Recent signups" icon={HiOutlineBolt}>
          <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
            {activity.recent_signups.map(u => (
              <div
                key={u.id}
                onClick={() => navigate(`/superadmin/subscribers/${u.id}`)}
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900 text-sm">{u.name || u.email}</p>
                  <p className="text-xs text-gray-500">{u.email} · {u.role}</p>
                </div>
                <span className="text-xs text-gray-500">{formatRelative(u.date_joined)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ─────────────────────────── 9. Quick actions ────────────────────────── */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-lg p-6 text-white">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <HiOutlineBolt className="w-5 h-5" /> Quick actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction icon={HiOutlineUsers} label="Manage Subscribers" sub="View all accounts and users" onClick={() => navigate('/superadmin/subscribers')} />
          <QuickAction icon={HiOutlineMegaphone} label="Send Notifications" sub="Broadcast to users" onClick={() => navigate('/superadmin/communications')} />
          <QuickAction icon={HiOutlineExclamationTriangle} label="Contact Messages" sub="Handle support requests" onClick={() => navigate('/superadmin/communications?tab=contact')} />
          <QuickAction icon={HiOutlineShieldCheck} label="Permissions" sub="Roles & security rules" onClick={() => navigate('/superadmin/permissions')} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────── building blocks ───────────────────────────

const LivePulse = () => (
  <span className="flex items-center gap-1.5 text-xs font-medium text-success bg-success-soft border border-success/30 rounded-full px-2 py-0.5">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-success-soft0" />
    </span>
    Live
  </span>
)

const Section = ({ title, icon: Icon, children }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-5 h-5 text-danger" />}
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    </div>
    {children}
  </section>
)

const StatCard = ({ title, value, icon: Icon, color, badge, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-lg shadow-sm p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5 text-white" /></div>
      {badge && <span className="px-2 py-0.5 text-[10px] font-semibold bg-red-100 text-danger rounded-full uppercase tracking-wide">{badge}</span>}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 leading-tight">{value ?? '—'}</h3>
    <p className="text-xs text-gray-600 mt-1">{title}</p>
  </div>
)

// ───────────────────────────── 1. Alerts strip ─────────────────────────────

const AlertsStrip = ({ subscriberCounts, cloudStats, navigate }) => {
  const alerts = useMemo(() => {
    const out = []
    if (subscriberCounts?.payment_due > 0) {
      out.push({
        tone: 'amber', icon: HiOutlineCreditCard,
        title: `${subscriberCounts.payment_due} subscription${subscriberCounts.payment_due === 1 ? '' : 's'} with payment overdue`,
        cta: 'Send reminders', onClick: () => navigate('/superadmin/subscribers?filter=payment_due'),
      })
    }
    const authRevoked24h = (cloudStats?.events_24h?.auth_revoked_fail || 0) + (cloudStats?.events_24h?.auth_revoked_success || 0)
    if (authRevoked24h > 0) {
      out.push({
        tone: 'red', icon: HiOutlineXCircle,
        title: `${authRevoked24h} cloud-storage connection${authRevoked24h === 1 ? '' : 's'} revoked in last 24h`,
        cta: 'View accounts', onClick: () => scrollIntoSection('cloud-storage'),
      })
    }
    if (cloudStats?.migrations?.failed_24h > 0) {
      out.push({
        tone: 'red', icon: HiOutlineExclamationTriangle,
        title: `${cloudStats.migrations.failed_24h} migration${cloudStats.migrations.failed_24h === 1 ? '' : 's'} failed in last 24h`,
        cta: 'View migrations', onClick: () => scrollIntoSection('cloud-storage'),
      })
    }
    if (cloudStats?.attention_accounts?.length > 0) {
      out.push({
        tone: 'amber', icon: HiOutlineBellAlert,
        title: `${cloudStats.attention_accounts.length} account${cloudStats.attention_accounts.length === 1 ? '' : 's'} need cloud-storage attention`,
        cta: 'Inspect', onClick: () => scrollIntoSection('cloud-storage'),
      })
    }
    return out
  }, [subscriberCounts, cloudStats, navigate])

  if (alerts.length === 0) {
    return (
      <div className="bg-success-soft border border-success/30 text-green-900 rounded-lg p-3 flex items-center gap-2 text-sm">
        <HiOutlineCheckCircle className="w-5 h-5 text-success flex-shrink-0" />
        <span><strong>All clear.</strong> No urgent system alerts right now.</span>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {alerts.map((a, i) => (
        <div key={i} className={`rounded-lg p-3 flex items-center justify-between gap-3 border ${
          a.tone === 'red' ? 'bg-danger-soft border-danger/30 text-red-900'
          : 'bg-warning-soft border-warning/30 text-amber-900'
        }`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <a.icon className={`w-5 h-5 flex-shrink-0 ${a.tone === 'red' ? 'text-danger' : 'text-warning'}`} />
            <span className="text-sm font-medium truncate">{a.title}</span>
          </div>
          {a.cta && (
            <button
              onClick={a.onClick}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${
                a.tone === 'red' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'
              } flex-shrink-0`}
            >
              {a.cta}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

const scrollIntoSection = (id) => {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// ────────────────────────────── 2. Pulse row ──────────────────────────────

const PulseRow = ({ activity }) => {
  const p = activity?.pulse
  if (!p) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-5 text-sm text-gray-500">
        Loading activity pulse…
      </div>
    )
  }
  return (
    <Section title={`Last ${activity.window_hours || 24} hours`} icon={HiOutlineBolt}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <PulseCard label="Signups" value={p.signups.count} delta={p.signups.delta} icon={HiOutlineUsers} color="bg-info-soft0" />
        <PulseCard label="Active accounts" value={p.active_accounts.count} delta={p.active_accounts.delta} icon={HiOutlineEye} color="bg-emerald-500" />
        <PulseCard label="New connections" value={p.new_connections.count} delta={p.new_connections.delta} icon={HiOutlineUserGroup} color="bg-indigo-500" />
        <PulseCard label="Uploads" value={p.uploads.count} delta={p.uploads.delta} icon={HiOutlineDocumentText} color="bg-brand-soft0" />
      </div>
    </Section>
  )
}

const PulseCard = ({ label, value, delta, icon: Icon, color }) => {
  const pct = delta?.percent
  const abs = delta?.absolute
  const isUp = (abs || 0) > 0
  const isDown = (abs || 0) < 0
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="w-4 h-4 text-white" /></div>
        {pct != null && (
          <span className={`flex items-center gap-1 text-xs font-medium ${
            isUp ? 'text-success' : isDown ? 'text-danger' : 'text-gray-500'
          }`}>
            {isUp ? <HiOutlineArrowTrendingUp className="w-3.5 h-3.5" /> : isDown ? <HiOutlineArrowTrendingDown className="w-3.5 h-3.5" /> : null}
            {pct >= 0 ? `+${pct}` : pct}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 leading-tight">{value}</h3>
      <p className="text-xs text-gray-600 mt-1">{label}</p>
    </div>
  )
}

// ──────────────────────────── 4. Cloud storage ─────────────────────────────

const CloudStorageSection = ({ stats, navigate }) => {
  if (!stats) {
    return (
      <Section title="Cloud storage health" icon={HiOutlineCloud}>
        <div className="bg-white rounded-lg shadow-sm p-5 text-sm text-gray-500">Loading…</div>
      </Section>
    )
  }

  const activeConnections = (stats.connections?.by_status || []).find(r => r.status === 'active')?.count || 0
  const revokedConnections = (stats.connections?.by_status || []).find(r => r.status === 'revoked')?.count || 0
  const driveFiles = (stats.files?.by_provider || []).find(r => r.storage_provider === 'google_drive') || {}
  const localFiles = (stats.files?.by_provider || []).find(r => r.storage_provider === 'local') || {}
  const runningMigrations = stats.migrations?.running_or_queued || 0
  const completed24h = stats.migrations?.completed_24h || 0
  const failed24h = stats.migrations?.failed_24h || 0

  return (
    <section id="cloud-storage">
      <div className="flex items-center gap-2 mb-3">
        <HiOutlineCloud className="w-5 h-5 text-danger" />
        <h2 className="text-base font-semibold text-gray-900">Cloud storage health</h2>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard title="Active connections" value={activeConnections} icon={HiOutlineCloud} color="bg-cyan-500" />
        <StatCard title="Revoked" value={revokedConnections} icon={HiOutlineXCircle} color="bg-danger-soft0" badge={revokedConnections > 0 ? 'Attention' : null} />
        <StatCard title="Files on Drive" value={driveFiles.count || 0} icon={HiOutlineCloudArrowUp} color="bg-info-soft0" />
        <StatCard title="Migrations running" value={runningMigrations} icon={HiOutlineArrowPath} color="bg-brand-soft0" badge={runningMigrations > 0 ? 'Live' : null} />
      </div>

      {/* Two-column: active migrations + accounts needing attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <PanelCard title="Active migrations" badge={runningMigrations}>
          {stats.migrations?.active?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {stats.migrations.active.map(m => (
                <div key={m.id} className="py-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => navigate(`/superadmin/subscribers/${m.account__id}`)}
                      className="font-medium text-gray-900 hover:text-danger"
                    >
                      {m.account__name}
                    </button>
                    <StatusPill status={m.status} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                    <span>{m.processed_files || 0} / {m.total_files || 0} files · {m.failed_files || 0} failed</span>
                    <span>{m.percent_complete}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-1.5">
                    <div className="bg-brand-soft0 h-1 rounded-full transition-all" style={{ width: `${m.percent_complete}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={HiOutlineCheckCircle} text="No active migrations." />
          )}
          {(completed24h > 0 || failed24h > 0) && (
            <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
              Last 24h: <strong className="text-success">{completed24h} completed</strong>
              {failed24h > 0 && <> · <strong className="text-danger">{failed24h} failed</strong></>}
            </p>
          )}
        </PanelCard>

        <PanelCard title="Accounts needing attention" badge={stats.attention_accounts?.length || 0}>
          {stats.attention_accounts?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {stats.attention_accounts.map((a, i) => (
                <div key={i} className="py-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => navigate(`/superadmin/subscribers/${a.account__id}`)}
                      className="font-medium text-gray-900 hover:text-danger truncate max-w-[60%]"
                    >
                      {a.account__name}
                    </button>
                    <StatusPill status={a.status} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {a.provider} · {a.provider_account_email || '—'}
                  </p>
                  {a.last_error && (
                    <p className="text-xs text-danger mt-1 line-clamp-2">{a.last_error}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={HiOutlineShieldCheck} text="All connections healthy." />
          )}
        </PanelCard>
      </div>

      {/* Events 24h + Recent issues */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PanelCard title="Events (24h)">
          <EventGrid events={stats.events_24h || {}} />
        </PanelCard>
        <div className="lg:col-span-2">
          <PanelCard title={`Recent issues (last 7 days)`} badge={stats.recent_issues?.length || 0}>
            {stats.recent_issues?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-100">
                      <th className="pb-2 pr-3 font-medium">When</th>
                      <th className="pb-2 pr-3 font-medium">Account</th>
                      <th className="pb-2 pr-3 font-medium">Event</th>
                      <th className="pb-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.recent_issues.slice(0, 8).map(r => (
                      <tr
                        key={r.id}
                        onClick={() => navigate(`/superadmin/subscribers/${r.account__id}`)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{formatRelative(r.created_at)}</td>
                        <td className="py-2 pr-3 text-gray-900 max-w-[120px] truncate">{r.account__name}</td>
                        <td className="py-2 pr-3"><EventPill event={r.event} /></td>
                        <td className="py-2 text-gray-600 truncate max-w-[260px]">{r.error_message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon={HiOutlineCheckCircle} text="No issues recorded in the last 7 days." />
            )}
          </PanelCard>
        </div>
      </div>
    </section>
  )
}

const ProviderBar = ({ row, totalBytes }) => {
  const pct = totalBytes > 0 ? Math.round((row.bytes || 0) * 100 / totalBytes) : 0
  const label = {
    local: 'OVACS local storage',
    google_drive: 'Google Drive',
    onedrive: 'OneDrive',
    dropbox: 'Dropbox',
  }[row.storage_provider] || row.storage_provider
  const color = {
    local: 'bg-gray-400',
    google_drive: 'bg-info-soft0',
    onedrive: 'bg-indigo-500',
    dropbox: 'bg-cyan-500',
  }[row.storage_provider] || 'bg-gray-400'
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-700 mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-gray-500">{row.count} files · {row.gb} GB · {pct}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

const EventGrid = ({ events }) => {
  const ROWS = [
    { key: 'connect_success', label: 'Connections', color: 'text-success' },
    { key: 'disconnect_success', label: 'Disconnections', color: 'text-gray-700' },
    { key: 'auth_revoked_fail', label: 'Auth revoked', color: 'text-danger' },
    { key: 'quota_exceeded_fail', label: 'Quota exceeded', color: 'text-warning' },
    { key: 'api_error_fail', label: 'API errors', color: 'text-danger' },
    { key: 'file_migrated_success', label: 'Files migrated', color: 'text-brand' },
    { key: 'file_migrated_fail', label: 'Migration failures', color: 'text-danger' },
  ]
  const hasAny = Object.values(events).some(v => v > 0)
  if (!hasAny) {
    return <EmptyState icon={HiOutlineCheckCircle} text="No cloud storage events in the last 24h." />
  }
  return (
    <div className="space-y-1.5">
      {ROWS.map(r => {
        const v = events[r.key] || 0
        if (!v) return null
        return (
          <div key={r.key} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{r.label}</span>
            <span className={`font-semibold ${r.color}`}>{v}</span>
          </div>
        )
      })}
    </div>
  )
}

// ──────────────────────────── 6. Top accounts ──────────────────────────────

const TopAccountsSection = ({ data, navigate }) => {
  if (!data) return null
  const byStorage = data.top_by_storage || []
  const byDrive = data.top_by_drive_files || []
  if (byStorage.length === 0 && byDrive.length === 0) return null

  return (
    <Section title="Top accounts" icon={HiOutlineArrowTrendingUp}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PanelCard title="Heaviest storage users">
          {byStorage.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {byStorage.map((a, i) => (
                <button
                  key={`${a.account__id}-${i}`}
                  onClick={() => navigate(`/superadmin/subscribers/${a.account__id}`)}
                  className="w-full py-2 flex items-center justify-between text-sm hover:bg-gray-50 px-2 -mx-2 rounded"
                >
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{i + 1}. {a.account__name}</p>
                    <p className="text-xs text-gray-500">{a.doc_count} files</p>
                  </div>
                  <span className="font-semibold text-gray-900">{a.total_gb} GB</span>
                </button>
              ))}
            </div>
          ) : <EmptyState icon={HiOutlineDocumentText} text="No storage data yet." />}
        </PanelCard>

        <PanelCard title="Most Drive-stored files">
          {byDrive.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {byDrive.map((a, i) => (
                <button
                  key={`${a.account__id}-${i}`}
                  onClick={() => navigate(`/superadmin/subscribers/${a.account__id}`)}
                  className="w-full py-2 flex items-center justify-between text-sm hover:bg-gray-50 px-2 -mx-2 rounded"
                >
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{i + 1}. {a.account__name}</p>
                    <p className="text-xs text-gray-500">{a.drive_gb} GB on Drive</p>
                  </div>
                  <span className="font-semibold text-brand">{a.drive_count} files</span>
                </button>
              ))}
            </div>
          ) : <EmptyState icon={HiOutlineCloud} text="No accounts using cloud storage yet." />}
        </PanelCard>
      </div>
    </Section>
  )
}

// ────────────────────────── presentational helpers ─────────────────────────

const PanelCard = ({ title, badge, children }) => (
  <div className="bg-white rounded-lg shadow-sm p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {badge != null && badge !== 0 && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">{badge}</span>
      )}
    </div>
    {children}
  </div>
)

const EmptyState = ({ icon: Icon, text }) => (
  <div className="text-center py-4 text-sm text-gray-500">
    {Icon && <Icon className="w-6 h-6 mx-auto mb-1.5 text-gray-300" />}
    {text}
  </div>
)

const StatusPill = ({ status }) => {
  const palette = {
    active: 'bg-green-100 text-green-800',
    revoked: 'bg-red-100 text-red-800',
    disconnected: 'bg-gray-100 text-gray-700',
    queued: 'bg-info-soft text-info',
    running: 'bg-brand-soft text-brand',
    paused: 'bg-amber-100 text-amber-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700',
  }[status] || 'bg-gray-100 text-gray-700'
  return <span className={`text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded ${palette}`}>{status}</span>
}

const EventPill = ({ event }) => {
  const palette = {
    auth_revoked: 'bg-red-100 text-red-800',
    quota_exceeded: 'bg-amber-100 text-amber-800',
    api_error: 'bg-orange-100 text-orange-800',
    file_migrated: 'bg-brand-soft text-brand',
    connect: 'bg-green-100 text-green-800',
    disconnect: 'bg-gray-100 text-gray-700',
  }[event] || 'bg-gray-100 text-gray-700'
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${palette}`}>{event}</span>
}

const GeoCard = ({ title, rows, dotColor }) => (
  <div className="bg-white rounded-lg shadow-sm p-5">
    <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {rows?.length > 0 ? rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 ${dotColor} rounded-full`} />
            <span className="text-gray-700">{r.country__name || 'Unknown'}</span>
          </div>
          <span className="font-semibold text-gray-900">{r.count}</span>
        </div>
      )) : <p className="text-sm text-gray-500">No data available.</p>}
    </div>
  </div>
)

const QuickAction = ({ icon: Icon, label, sub, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 transition-colors text-left flex flex-col gap-1"
  >
    <Icon className="w-5 h-5 mb-1" />
    <p className="font-medium text-sm">{label}</p>
    <p className="text-xs text-red-100">{sub}</p>
  </button>
)

function formatRelative(iso) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, Math.floor((now - then) / 1000))
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  const days = Math.floor(diff / 86400)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export default SuperAdminDashboard
